<template>
    <div class="af3-shotcoder-filter-bar-numbers-container">
        <label :for="`af3-shotcoder-filter-bar-numbers-${type}`">{{ type }}</label>

        <div class="af3-shotcoder-filter-bar-numbers-container-buttons">
            <button :disabled="selectedValue <= (min - 1)" @click="handleCLickSubtract">-</button>
            <span>{{ handleStringAny() }}</span>
            <button :disabled="isMaxValue" @click="handleCLickAdd">+</button>
        </div>
    </div>
</template>
  
<script>

import { defineComponent, ref, watch, onMounted, reactive, computed } from 'vue';


export default defineComponent({
    components: { },
   
    props: {
        min: {
            type: Number,
            required: true
        },
        max: {
            type: Number,
            required: true
        },
        type: {
            type: String,
            required: true
        },
        value: {
            type: Number,
            required: false,
            default: null
        },
        available_values: {
            type: Array,
            required: true
        }
    },
    
    emits: ['update:value'],
    
    setup(props, { emit }) {
        console.log('NumberButtonsComponent setup', props.type, props.min, props.max, props.value);
        console.log('Available values:', props.available_values);
        
        // Initialize with value if provided, otherwise use min-1 (representing "Any")
        const selectedValue = ref(props.value !== null ? Number(props.value) : props.min - 1);
        
        // Debug logging
        console.log('Initial selected value', selectedValue.value);

        // Sort available values to ensure they're in order and convert to numbers
        const sortedAvailableValues = computed(() => {
            // Convert all values to numbers to ensure correct comparisons
            const numberedValues = props.available_values.map(val => Number(val));
            const sorted = [...numberedValues].sort((a, b) => a - b);
            console.log(`Sorted available values for ${props.type}:`, sorted);
            return sorted;
        });

        // Computed property to check if we're at the maximum value
        const isMaxValue = computed(() => {
            // If no available values, disable button
            if (sortedAvailableValues.value.length === 0) {
                return true;
            }
            
            if (selectedValue.value === props.min - 1) {
                // If at "Any", button should be enabled if there are available values
                return false;
            }
            
            // Find current index
            const index = sortedAvailableValues.value.indexOf(selectedValue.value);
            console.log(`isMaxValue check: value=${selectedValue.value}, index=${index}, length=${sortedAvailableValues.value.length}`);
            
            // If value not found or is the last one, disable button
            return index === -1 || index === sortedAvailableValues.value.length - 1;
        });

        const handleCLickAdd = () => {
            console.log(`handleClickAdd for ${props.type}. Current value: ${selectedValue.value}`);
            console.log(`Available values: ${sortedAvailableValues.value.join(', ')}`);
            
            if (sortedAvailableValues.value.length === 0) {
                // No values to navigate to
                return;
            }
            
            if (selectedValue.value === props.min - 1) {
                // If currently at "Any", set to the first available value
                console.log(`Setting from Any to first value: ${sortedAvailableValues.value[0]}`);
                selectedValue.value = sortedAvailableValues.value[0];
            } else {
                // Find current index in available values
                const currentIndex = sortedAvailableValues.value.indexOf(selectedValue.value);
                console.log(`Current index: ${currentIndex} out of ${sortedAvailableValues.value.length - 1}`);
                
                // If not found in array, start with first value
                if (currentIndex === -1) {
                    console.log(`Value ${selectedValue.value} not found in available values, setting to first: ${sortedAvailableValues.value[0]}`);
                    selectedValue.value = sortedAvailableValues.value[0];
                } else if (currentIndex < sortedAvailableValues.value.length - 1) {
                    // Move to next available value
                    console.log(`Moving from ${selectedValue.value} to ${sortedAvailableValues.value[currentIndex + 1]}`);
                    selectedValue.value = sortedAvailableValues.value[currentIndex + 1];
                }
            }
            
            // Emit the updated value
            console.log(`${props.type}: Emitting value after add: ${selectedValue.value}`);
            emit('update:value', selectedValue.value);
        }

        const handleCLickSubtract = () => {
            console.log(`handleClickSubtract for ${props.type}. Current value: ${selectedValue.value}`);
            console.log(`Available values: ${sortedAvailableValues.value.join(', ')}`);
            
            // Don't go below min-1
            if (selectedValue.value <= props.min - 1) {
                selectedValue.value = props.min - 1;
                return;
            }
            
            // Find current index in available values
            const currentIndex = sortedAvailableValues.value.indexOf(selectedValue.value);
            console.log(`Current index: ${currentIndex}`);
            
            // If not found in array or it's the first value, go to "Any"
            if (currentIndex === -1 || currentIndex === 0) {
                console.log(`Setting to Any from ${selectedValue.value}`);
                selectedValue.value = props.min - 1;
            } else if (currentIndex > 0) {
                // Move to previous available value
                console.log(`Moving from ${selectedValue.value} to ${sortedAvailableValues.value[currentIndex - 1]}`);
                selectedValue.value = sortedAvailableValues.value[currentIndex - 1];
            }
            
            // Emit the updated value
            console.log(`${props.type}: Emitting value after subtract: ${selectedValue.value}`);
            emit('update:value', selectedValue.value);
        }

        const handleStringAny = () => {
            if(selectedValue.value <= props.min - 1) {
                return 'Any';
            }
            
            if(props.type === 'bedrooms'){
                if(selectedValue.value === 0){
                    return 'Studio';
                }
                return selectedValue.value;
            }
            
            return selectedValue.value;
        }
        
        // Watch for changes in value prop
        watch(() => props.value, (newVal) => {
            console.log(`${props.type} value changed`, newVal);
            if (newVal !== null) {
                selectedValue.value = Number(newVal);
            } else {
                selectedValue.value = props.min - 1; // Default to min-1 (Any) if value is null
            }
        });
        
        // Watch for changes in min/max range props
        watch(() => props.min, (newVal) => {
            console.log(`${props.type} min changed`, newVal);
            if (props.value === null) {
                selectedValue.value = Number(newVal) - 1; // Reset to "Any" state
            }
        });
        
        // Watch for changes in available values
        watch(() => props.available_values, (newValues) => {
            console.log(`${props.type} available_values changed:`, newValues);
        });
        
        // Watch for changes in selected value to emit updates
        watch(selectedValue, (newVal) => {
            console.log(`${props.type}: selectedValue changed to ${newVal} (min-1: ${props.min - 1})`);
            // Only emit if it's not the default "Any" value
            if (newVal !== props.min - 1) {
                console.log(`${props.type}: Emitting actual value: ${newVal}`);
                emit('update:value', newVal);
            } else {
                // For "Any" value, emit null
                console.log(`${props.type}: Emitting null for "Any" value`);
                emit('update:value', null);
            }
        });
        
        // Initialize component
        onMounted(() => {
            console.log('NumberButtonsComponent mounted', props.type, selectedValue.value);
            console.log('Available values at mount:', sortedAvailableValues.value);
            
            // Log more details about the available values
            if (sortedAvailableValues.value.length === 0) {
                console.warn(`Warning: No available values for ${props.type}!`);
            } else if (sortedAvailableValues.value.length === 1) {
                console.warn(`Note: Only one available value for ${props.type}: ${sortedAvailableValues.value[0]}`);
            }
            
            // Ensure initial values are valid
            if (selectedValue.value > props.max) {
                selectedValue.value = props.max;
            }
            
            // Force a refresh of the values to ensure they're properly initialized
            if (props.value === null) {
                selectedValue.value = props.min - 1;
            }
            
            // Emit initial value if not "Any"
            if (selectedValue.value !== props.min - 1) {
                emit('update:value', selectedValue.value);
            }
        });
          
        return {
          selectedValue,
          handleCLickAdd,
          handleCLickSubtract,
          handleStringAny,
          isMaxValue,
          sortedAvailableValues
        };
    },
});
</script>